// ── 세금계산서 연결 팝업 ──────────────────────────────────────────────────────
import React from 'react';
import { fmtNum, fmtDate } from './styles';

// 구분 배지 색상
const TYPE_COLOR = {
  '청구': { bg: '#fef3c7', color: '#92400e' },
  '영수': { bg: '#dcfce7', color: '#15803d' },
  '취소': { bg: '#fee2e2', color: '#b91c1c' },
  '수정': { bg: '#dbeafe', color: '#1d4ed8' },
};

/**
 * 입금 탭 "미연결" 버튼 클릭 시 나타나는 세금계산서 선택 팝업
 * position:fixed — 버튼 우측에 붙어서 뜸
 *
 * Props:
 *   linkPopup   — { paymentId, top, left }
 *   taxInvoices — 전체 세금계산서 목록 (미입금액 > 0 필터 내부에서)
 *   onSelect(invoiceId) — 항목 선택 시
 *   onClose     — 배경 클릭 시 닫기
 */
export function LinkPopup({ linkPopup, taxInvoices, onSelect, onClose }) {
  if (!linkPopup) return null;

  // 미처리 잔액이 있는 항목 모두 연결 가능. 양수 = 미입금, 음수 = 환불 입금 미연결.
  const linkable = taxInvoices.filter(
    (inv) => Number(inv.unpaid_amount ?? inv.total_amount) !== 0
  );

  return (
    <>
      {/* 배경 클릭 시 닫기 */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 1500 }}
        onClick={onClose}
      />

      {/* 팝업 본체 */}
      <div style={{
        position: 'fixed',
        top:  linkPopup.top,
        left: linkPopup.left,
        zIndex: 1501,
        background: 'var(--bg-card)',
        borderRadius: 10,
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        border: '1px solid var(--border)',
        minWidth: 260,
        maxWidth: 340,
        maxHeight: 320,
        overflowY: 'auto',
        padding: '8px 0',
      }}>
        {/* 팝업 헤더 */}
        <div style={{
          padding: '6px 14px 8px',
          fontSize: 12, fontWeight: 700, color: 'var(--text-sub)',
          borderBottom: '1px solid var(--bg-subtle)',
        }}>
          세금계산서 선택
        </div>

        {linkable.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12, color: 'var(--text-muted)' }}>
            연결 가능한 세금계산서가 없습니다.
          </div>
        ) : (
          linkable.map((inv) => {
            const unpaid    = Number(inv.unpaid_amount ?? inv.total_amount);
            const typeColor = TYPE_COLOR[inv.issue_type] || { bg: 'var(--bg-subtle)', color: 'var(--text-sub)' };

            return (
              <button
                key={inv.id}
                onClick={() => onSelect(inv.id)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 3,
                  width: '100%', textAlign: 'left',
                  padding: '8px 14px', border: 'none', background: 'none',
                  cursor: 'pointer', borderBottom: '1px solid var(--bg-subtle)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f9ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>

                {/* 발행일 */}
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {fmtDate(inv.issue_date)}
                </span>

                {/* 상호 + 구분 배지 */}
                <span style={{ fontSize: 12, color: 'var(--text-sub)', fontWeight: 600 }}>
                  {inv.customer_name || '(상호 없음)'}
                  <span style={{
                    marginLeft: 6, fontSize: 11,
                    padding: '1px 6px', borderRadius: 4,
                    background: typeColor.bg, color: typeColor.color,
                  }}>
                    {inv.issue_type}
                  </span>
                </span>

                {/* 발행금액 · 미처리 잔액 */}
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  발행 {fmtNum(inv.total_amount)}원
                  <span style={{
                    marginLeft: 8,
                    color: unpaid < 0 ? '#b91c1c' : '#0369a1',
                    fontWeight: 600,
                  }}>
                    {unpaid < 0 ? `환불 ${fmtNum(Math.abs(unpaid))}원 연결 필요` : `미입금 ${fmtNum(unpaid)}원`}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}
